import React from 'react';
import { summarizeSleep, DURATION_MAX, EFFICIENCY_MAX, CONTINUITY_MAX, SLEEP_STAGES } from '../utils/sleepSummary';

// 앱의 수면 카드(SmartRingSleepSection.kt)를 웹으로 옮긴 것이다.
// 색·구성·산식을 앱과 같게 유지한다 — 보호자가 센터에서 보는 수면과
// 사용자가 앱에서 보는 수면이 다르면 안 된다.

const STAGE_COLORS = {
    [SLEEP_STAGES.AWAKE]: '#FFC979',
    [SLEEP_STAGES.RESTLESS]: '#FFE3B8',
    [SLEEP_STAGES.REM]: '#DCCBFF',
    [SLEEP_STAGES.LIGHT]: '#9782F6',
    [SLEEP_STAGES.DEEP]: '#6B18FF'
};

const STAGE_LABELS = {
    [SLEEP_STAGES.AWAKE]: '깨어 있음',
    [SLEEP_STAGES.RESTLESS]: '뒤척임',
    [SLEEP_STAGES.REM]: 'REM',
    [SLEEP_STAGES.LIGHT]: '얕은 수면',
    [SLEEP_STAGES.DEEP]: '깊은 수면'
};

// 히프노그램 세로 위치. 앱과 같은 배치 — 뒤척임과 지속 각성은 둘 다
// '깨어 있음'이라 같은 높이에 두고 색으로만 구분한다.
const STAGE_Y = {
    [SLEEP_STAGES.AWAKE]: 0.16,
    [SLEEP_STAGES.RESTLESS]: 0.16,
    [SLEEP_STAGES.REM]: 0.35,
    [SLEEP_STAGES.LIGHT]: 0.56,
    [SLEEP_STAGES.DEEP]: 0.78
};

const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours === 0) return `${rest}분`;
    return `${hours}시간 ${rest}분`;
};

const formatClock = (epochSeconds) => new Date(epochSeconds * 1000).toLocaleTimeString('ko-KR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Seoul'
});

const formatPercent = (minutes, periodMinutes) => (
    periodMinutes > 0 ? `${Math.round((minutes / periodMinutes) * 100)}%` : '0%'
);

// 'yyyy-mm-dd' → '2026년 8월 11일'. 앱 카드와 같은 표기.
const formatCardDate = (dateKey) => {
    const [year, month, day] = String(dateKey).split('-').map(Number);
    if (!year || !month || !day) return dateKey;
    return `${year}년 ${month}월 ${day}일`;
};

function ScoreRing({ score }) {
    const radius = 44;
    const stroke = 9;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(Math.max(score, 0), 100) / 100;

    return (
        <div className="sleep-score-ring">
            <svg width="104" height="104" viewBox="0 0 104 104">
                <circle
                    cx="52" cy="52" r={radius} fill="none"
                    stroke="rgba(255,255,255,0.18)" strokeWidth={stroke}
                />
                <circle
                    cx="52" cy="52" r={radius} fill="none"
                    stroke={STAGE_COLORS[SLEEP_STAGES.DEEP]} strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={`${circumference * progress} ${circumference}`}
                    transform="rotate(-90 52 52)"
                />
            </svg>
            <div className="sleep-score-ring-text">
                <span className="sleep-score-value">{score}</span>
                <span className="sleep-score-suffix">점</span>
            </div>
        </div>
    );
}

function ScorePart({ label, points, max }) {
    const fraction = max > 0 ? Math.min(points / max, 1) : 0;
    return (
        <div className="sleep-score-part">
            <span className="sleep-score-part-label">{label}</span>
            <span className="sleep-score-part-points">{points}/{max}</span>
            <div className="sleep-bar-track sleep-bar-track-thin">
                <div
                    className="sleep-bar-fill"
                    style={{ width: `${fraction * 100}%`, background: STAGE_COLORS[SLEEP_STAGES.DEEP] }}
                />
            </div>
        </div>
    );
}

// 앱과 같은 형태의 히프노그램. 구간을 단계 높이에 수평선으로 긋고,
// 인접 구간은 가는 선으로 잇는다.
function Hypnogram({ summary }) {
    const width = 600;
    const height = 150;
    const range = Math.max(summary.endSeconds - summary.startSeconds, 1);
    const x = (seconds) => ((seconds - summary.startSeconds) / range) * width;

    const lines = [];
    let previous = null;
    summary.timeline.forEach((entry, index) => {
        const y = STAGE_Y[entry.stage] * height;
        const startX = x(entry.startSeconds);
        const endX = x(entry.endSeconds);
        if (previous) {
            lines.push(
                <line
                    key={`join-${index}`}
                    x1={previous.endX} y1={previous.y} x2={startX} y2={y}
                    stroke={STAGE_COLORS[entry.stage]} strokeWidth="3"
                />
            );
        }
        lines.push(
            <line
                key={`seg-${index}`}
                x1={startX} y1={y} x2={endX} y2={y}
                stroke={STAGE_COLORS[entry.stage]} strokeWidth="13" strokeLinecap="round"
            />
        );
        previous = { endX, y };
    });

    return (
        <svg
            className="sleep-hypnogram"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="수면 단계 그래프"
        >
            {lines}
        </svg>
    );
}

function StageBar({ stage, minutes, periodMinutes }) {
    const fraction = periodMinutes > 0 ? Math.min(minutes / periodMinutes, 1) : 0;
    return (
        <div className="sleep-stage-bar">
            <span className="sleep-stage-bar-label">
                {STAGE_LABELS[stage]} {formatDuration(minutes)} {formatPercent(minutes, periodMinutes)}
            </span>
            <div className="sleep-bar-track">
                <div
                    className="sleep-bar-fill"
                    style={{ width: `${fraction * 100}%`, background: STAGE_COLORS[stage] }}
                />
            </div>
        </div>
    );
}

/**
 * 하루치 수면 카드. daily 응답의 sleep(세그먼트 포함)을 받는다.
 * 세그먼트가 없으면(includeSleepSegments 미요청·데이터 없음) 렌더하지 않는다 —
 * 부모가 안내 문구를 책임진다.
 */
export default function SleepCard({ date, sleep }) {
    const summary = summarizeSleep(sleep?.segments);
    if (!summary) return null;

    const stageOrder = [
        SLEEP_STAGES.AWAKE, SLEEP_STAGES.RESTLESS,
        SLEEP_STAGES.REM, SLEEP_STAGES.LIGHT, SLEEP_STAGES.DEEP
    ];
    const stageMinutes = {
        [SLEEP_STAGES.AWAKE]: summary.awakeMinutes,
        [SLEEP_STAGES.RESTLESS]: summary.restlessMinutes,
        [SLEEP_STAGES.REM]: summary.remMinutes,
        [SLEEP_STAGES.LIGHT]: summary.lightMinutes,
        [SLEEP_STAGES.DEEP]: summary.deepMinutes
    };

    return (
        <div className="sleep-card">
            <div className="sleep-card-header">
                <div>
                    <div className="sleep-card-title">잠</div>
                    <div className="sleep-card-date">{formatCardDate(date)}</div>
                </div>
            </div>

            <div className="sleep-card-hero">
                <div>
                    <div className="sleep-card-hero-label">총 수면</div>
                    <div className="sleep-card-hero-value">{formatDuration(summary.sleepMinutes)}</div>
                    <div className="sleep-card-hero-range">
                        {formatClock(summary.startSeconds)}-{formatClock(summary.endSeconds)}
                    </div>
                </div>
                <ScoreRing score={summary.score} />
            </div>

            <div className="sleep-card-meta">
                <div className="sleep-card-meta-item">
                    <span className="sleep-card-meta-value">{summary.efficiencyPercent}%</span>
                    <span className="sleep-card-meta-label">수면 효율</span>
                </div>
                <div className="sleep-card-meta-item">
                    <span className="sleep-card-meta-value">{summary.quality}</span>
                    <span className="sleep-card-meta-label">수면의 질</span>
                </div>
            </div>

            <div className="sleep-score-parts">
                <ScorePart label="시간" points={summary.scoreBreakdown.durationPoints} max={DURATION_MAX} />
                <ScorePart label="효율" points={summary.scoreBreakdown.efficiencyPoints} max={EFFICIENCY_MAX} />
                <ScorePart label="연속성" points={summary.scoreBreakdown.continuityPoints} max={CONTINUITY_MAX} />
            </div>
            <div className="sleep-awakening-note">
                {summary.awakeningCount > 0
                    ? `수면 중 ${summary.awakeningCount}회 깼습니다`
                    : '수면 중 깬 기록이 없습니다'}
            </div>

            <div className="sleep-card-divider" />

            <div className="sleep-legend">
                {stageOrder.map((stage) => (
                    <span key={stage} className="sleep-legend-item">
                        <span className="sleep-legend-dot" style={{ background: STAGE_COLORS[stage] }} />
                        {STAGE_LABELS[stage]}
                    </span>
                ))}
            </div>

            <Hypnogram summary={summary} />
            <div className="sleep-hypnogram-times">
                <span>{formatClock(summary.startSeconds)}</span>
                <span>{formatClock(summary.endSeconds)}</span>
            </div>

            <div className="sleep-stage-bars">
                {stageOrder.map((stage) => (
                    <StageBar
                        key={stage}
                        stage={stage}
                        minutes={stageMinutes[stage]}
                        periodMinutes={summary.periodMinutes}
                    />
                ))}
            </div>
        </div>
    );
}
